import moment from 'moment-timezone';
import {
  AUTH_TOKEN_KEYS,
  ROLE,
  USER_TYPE,
  USER_ROLE_LABELS,
  DEFAULT_USER_AVATAR,
  MEDIA_TYPE,
  PURCHASE_STATE,
  PAYMENT_INFO,
  OFFER_TYPE
} from '../constants/common';
import { MIN_AGE } from '../constants/valisdation';
import { HEIC_EXTENSIONS, HEIC_MIME_TYPES, VIDEO_EXTENSIONS } from '../constants/media';
import { getLocalItem } from './config/localStorage';
import { phoneFormats } from './phoneFormats';
import { ROUTES } from '../constants/routes';
import { reportError } from '../sentry';

/** IANA timezone for subscription dates (Arizona does not observe DST). */
export const SUBSCRIPTION_TIMEZONE = 'America/Phoenix';
export const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Determines the redirect path based on user role and optional redirect URL
 * @param {number|string} userRole - The user's role identifier
 * @param {string|null} redirectUrl - Optional specific redirect URL
 * @returns {string} - The path to redirect the user to
 */
export const getRedirectPath = (userRole, redirectUrl = null) => {
  if (redirectUrl) {
    return `/${redirectUrl}`;
  }

  // Convert role to number for comparison since it might be stored as string
  const roleNumber = parseInt(userRole, 10);

  if (roleNumber === ROLE.CREATOR_SUB || roleNumber === ROLE.CREATOR_CONTENT) {
    return ROUTES.MY_PROFILE;
  } else {
    return ROUTES.DISCOVER;
  }
};

/**
 * Capitalizes the first letter of a string for UI display
 * @param {string} str - The string to capitalize
 * @returns {string} - The string with first letter capitalized
 */
export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} str - The string to capitalize
 * @returns {string} - The string with each word's first letter capitalized
 */
export const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return str;
  return str.split(" ").map(word => capitalizeFirstLetter(word)).join(" ");
};

/**
 * Formats a number with appropriate suffix (k, M, G, etc.)
 * @param {number|string} num - The number to format
 * @param {number} digits - Number of decimal places (default: 1)
 * @param {string} singular - Singular form of the unit (default: "")
 * @param {string} plural - Plural form of the unit (default: "")
 * @returns {string} - Formatted number with suffix and unit
 */
export const nFormatter = (num, digits = 1, singular = "", plural = "") => {
  const numericValue = parseFloat(num) || 0;

  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];

  const trailingZerosRegex = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find(item => numericValue >= item.value);

  const pluralLabel = plural || "";
  const singularLabel = singular || "";
  const formattedNumber = item
    ? (numericValue / item.value).toFixed(digits).replace(trailingZerosRegex, "$1") + item.symbol
    : "0";

  const unit = numericValue === 0 ? pluralLabel : numericValue > 1 ? pluralLabel : singularLabel;

  return `${formattedNumber} ${unit}`.trim();
};

/**
 * Formats a date string to a localized date string (uses browser timezone)
 * @param {string} dateString - The date string to format
 * @param {string} [format=''] - Format style: 'MM DD, YYYY' (e.g. "03 07, 2026"), 'Month DD, YYYY' (e.g. "March 07, 2026"), 'Mon DD' (e.g. "Apr 05"), or empty for short date (e.g. "Mar 7")
 * @returns {string} - The formatted date string
 */
export const formatDateTimeZone = (dateString, format = '') => {
  if (!dateString) return '';
  const timezone = BROWSER_TIMEZONE;
  const date = new Date(dateString);

  if (format === 'MM DD, YYYY') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .format(date)
      .replace(/\//g, ' ')
      .replace(/\s+(\d{4})$/, ', $1');
  } else if (format === 'Month DD, YYYY') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    }).format(date);
  } else if (format === 'Mon DD') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: '2-digit',
    }).format(date);
  } else {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
    }).format(date);
  }
};

/**
 * Formats a timestamp to abbreviated relative time format
 * @param {string|Date|number} timestamp - The timestamp to format
 * @returns {string} - Abbreviated time format (e.g., "now", "1m", "1h", "2d", "1w", "1mo", "1y")
 */
export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';

  const now = moment();
  const time = moment(timestamp);

  if (!time.isValid()) {
    return '';
  }

  const diff = now.diff(time, 'seconds');

  if (diff < 60) {
    return 'now';
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60);
    return `${minutes}m`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours}H`;
  } else if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days}D`;
  } else if (diff < 2592000) {
    const weeks = Math.floor(diff / 604800);
    return `${weeks}W`;
  } else if (diff < 31536000) {
    const months = Math.floor(diff / 2592000);
    return `${months}M`;
  } else {
    const years = Math.floor(diff / 31536000);
    return `${years}Y`;
  }
};

/**
 * Gets the relative time string (Today, Yesterday, X days ago, X months ago, X years ago)
 * @param {string|Date} dateString - The date string to calculate relative time for
 * @returns {string} - Relative time string (e.g., "Today", "Yesterday", "5 days ago", "2 months ago")
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month ago';
    if (diffMonths < 12) return `${diffMonths} months ago`;

    const diffYears = Math.floor(diffMonths / 12);
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  } catch (error) {
    reportError(error, 'relative time', { extra: { dateString } });
    return '';
  }
};


/**
 * Converts military time (24-hour format) to standard time (12-hour format)
 * @param {string} time - Time in HH:MM format (24-hour)
 * @returns {string} - Time in 12-hour format with AM/PM
 */
export const convertToStandardTime = (time) => {
  if (!time || typeof time !== 'string') {
    return '';
  }

  const timeParts = time.split(':');
  if (timeParts.length !== 2) {
    return time;
  }

  const hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];

  if (isNaN(hours) || hours < 0 || hours > 23) {
    return time;
  }

  if (hours === 0 || hours === 24) {
    return `12:${minutes} AM`;
  }

  if (hours < 12) {
    return `${hours}:${minutes} AM`;
  }

  if (hours === 12) {
    return `12:${minutes} PM`;
  }

  return `${hours - 12}:${minutes} PM`;
};

export const validateUsername = (username) => {
  return !/[^0-9a-zA-Z]/.test(username);
};

export const validateEmail = (email) => {
  return email.indexOf("@") > 0 && email.includes(".");
};

export const sanitizeNameInput = (value) => {
  return value.replace(/[^a-zA-Z\s]/g, "");
};

export const sanitizeUsernameInput = (value) => {
  return value.replace(/[^a-zA-Z0-9]/g, "");
};

/**
 * Calculates the maximum allowed date of birth (18 years ago from today)
 * This ensures users are at least 18 years old
 * @returns {string} - Date in YYYY-MM-DD format for HTML date input max attribute
 */
export const getMaxDateOfBirth = () => {
  const today = new Date();
  const year = today.getFullYear() - MIN_AGE;
  const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(today.getDate()).padStart(2, '0');

  // Return in YYYY-MM-DD format for HTML5 date input compatibility
  return `${year}-${month}-${day}`;
};

/**
 * Calculates the minimum allowed date of birth (120 years ago from today)
 * This prevents unrealistic age entries
 * @returns {string} - Date in YYYY-MM-DD format for HTML date input min attribute
 */
export const getMinDateOfBirth = () => {
  const today = new Date();
  const year = today.getFullYear() - 120; // Maximum reasonable age
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  // Return in YYYY-MM-DD format for HTML5 date input compatibility
  return `${year}-${month}-${day}`;
};


/**
 * Formats a phone number according to country-specific rules
 * @param {string|number} input - The phone number to format
 * @param {string} format - Format type ("bracket" or "dash")
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB', 'IN')
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (input, format = "dash", countryCode = "US") => {
  if (!input) return "";

  // Convert to string to handle cases where input might be a number or other type
  const inputString = String(input);

  // Get country-specific format or default to US
  const countryFormat = phoneFormats[countryCode] || phoneFormats['US'];

  // Sanitize + limit to country-specific max length
  const digits = inputString.replace(/\D/g, "").slice(0, countryFormat.maxLength);

  if (format === "bracket") {
    return countryFormat.format(digits);
  }

  // Default to "dash" format (backwards compatible)
  const [area, prefix, line] = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 10),
  ];

  if (digits.length <= 3) return area;
  if (digits.length <= 6) return `${area} - ${prefix}`;
  return `${area} - ${prefix} - ${line}`;
};

/**
 * Gets the placeholder for a specific country's phone number format
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB', 'IN')
 * @returns {string} - Placeholder text for the phone number input
 */
export const getPhonePlaceholder = (countryCode = "US") => {
  const countryFormat = phoneFormats[countryCode] || phoneFormats['US'];
  return countryFormat.placeholder || '(555) 123-4567';
};

export const removePhoneNumberFormatting = (value) => {
  // Remove all non-digit characters
  return value.replace(/\D/g, "");
};

export const isUserLoggedIn = () => {
  const token = getLocalItem('accessToken', null);
  if (token) {
    return true;
  } else {
    return false;
  }
};

const parseAccessTokenPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    reportError(error, 'parse access token payload');
    return null;
  }
};

/** True when the current session was created with an admin support password. */
export const isLoginWithSupportPassword = () => {
  const stored = getLocalItem(AUTH_TOKEN_KEYS.IS_LOGIN_WITH_SUPPORT_PASSWORD, null);
  if (stored === true || stored === 'true') {
    return true;
  }
  if (stored === false || stored === 'false') {
    return false;
  }

  const payload = parseAccessTokenPayload(getLocalItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN, null));
  return payload?.isLoginWithSupportPassword === true;
};

export const getUserRole = () => {
  const role = getLocalItem('role', null);
  return role;
};

export const getUserId = () => {
  const userId = getLocalItem('userId', null);
  return userId;
};

export const isUserContentCreator = () => {
  const userRole = getUserRole();
  return userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
};

export const isUserAdmin = () => {
  const userRole = getUserRole();
  if (userRole == null) return false;
  return Number(userRole) === ROLE.ADMIN;
};

export const getUserStatus = () => {
  const role = getUserRole();
  return role == ROLE.CREATOR_SUB || role == ROLE.CREATOR_CONTENT
    ? USER_TYPE.CREATOR
    : role == ROLE.USER
      ? USER_TYPE.USER
      : role == ROLE.ADMIN
        ? USER_TYPE.ADMIN
        : USER_TYPE.GUEST;
};

// Function to get profile type based on user role
export const getProfileTypeFromRole = (role) => {
  if (role === ROLE.CREATOR_SUB) {
    return USER_ROLE_LABELS.SUBSCRIBER;
  } else if (role === ROLE.CREATOR_CONTENT) {
    return USER_ROLE_LABELS.FOLLOWER;
  }
  return null;
};

/**
 * Gets the user's profile picture or returns a default picture
 * @param {Object} user - The user object containing profile picture URL
 * @returns {string} - The profile picture URL
 */
export const getUserProfilePicture = (user, creator) => {
  if (isUserLoggedIn()) {
    const userStatus = getUserStatus();
    if (userStatus === USER_TYPE.CREATOR && creator && creator?.user_data?.profile_picture) {
      return creator.user_data.profile_picture;
    }
    if (user && user?.profile_picture) {
      return user.profile_picture;
    }
  }
  return DEFAULT_USER_AVATAR;
};

const getFileExtension = (file) =>
  file?.name?.split('.').pop()?.toLowerCase() || '';

/**
 * Checks if a file is HEIC/HEIF format
 * @param {File} file - The file object to check
 * @returns {boolean}
 */
export const isHeicFile = (file) => {
  if (!file) return false;
  if (HEIC_MIME_TYPES.has(file.type)) return true;
  return HEIC_EXTENSIONS.has(getFileExtension(file));
};

/**
 * Checks if a URL points to a HEIC/HEIF image (by path extension).
 * @param {string} url
 * @returns {boolean}
 */
export const isHeicUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const path = url.split('?')[0].split('#')[0];
  const ext = path.split('.').pop()?.toLowerCase() || '';
  return HEIC_EXTENSIONS.has(ext);
};

/**
 * Checks if a file is an image based on its MIME type
 * @param {File} file - The file object to check
 * @returns {boolean} - True if the file is an image, false otherwise
 */
export const isImage = (file) => {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  return isHeicFile(file);
};

/**
 * Checks if a file is a video based on its MIME type or extension.
 * @param {File} file - The file object to check
 * @returns {boolean} - True if the file is a video, false otherwise
 */
export const isVideo = (file) => {
  if (!file) return false;
  if (file.type?.startsWith('video/')) return true;
  return VIDEO_EXTENSIONS.has(getFileExtension(file));
};

export const getDaysFromSeconds = (seconds) => {
  return Math.round(seconds / 86400);
};

export const subsString = (count) => {
  if (count === 1) return '';
  return 's';
};

export const formatProfileTag = (str) => {
  if (!str || typeof str !== 'string') return str;
  if (str.includes('-')) {
    return str.split('-').map(capitalizeFirstLetter).join(' ');
  }
  return capitalizeFirstLetter(str);
};

export const formatDuration = (seconds) => {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Checks if a post is bundle content
 * @param {Object} post - The post object to check
 * @returns {boolean} - True if the post is bundle content, false otherwise
 */
export const isBundleContent = (post) => {
  if (!post) return false;

  const hasMultipleAssets = post.assets && Array.isArray(post.assets) && post.assets.length > 1;
  return (
    post.type_content === MEDIA_TYPE.BUNDLE ||
    post.type_file === MEDIA_TYPE.BUNDLE ||
    post.content_type === MEDIA_TYPE.BUNDLE ||
    post.isBundle ||
    hasMultipleAssets
  );
};

/**
 * Get primary (cover) media src and poster from post using assets.
 * @param {Object} post - The post object
 * @returns {{ src: string|null, poster: string|null, type: string }}
 */
export const getPrimaryMedia = (post) => {
  if (!post?.assets?.length) {
    return { src: null, poster: null, type: post?.type_file };
  }
  const idx = Math.min(Math.max(0, post.cover_index ?? 0), post.assets.length - 1);
  const asset = post.assets[idx];
  return {
    src: asset.key ?? null,
    poster: asset.poster ?? null,
    type: asset.type || post.type_file
  };
};

/**
 * Gets the media array for a post (handles bundle and regular content)
 * @param {Object} post - The post object
 * @returns {Array|null} - Media array or null if not bundle content
 */
export const getMediaArray = (post) => {
  if (!post) return null;

  // If it's bundle content, construct or return media array
  if (isBundleContent(post)) {
    const hasAssets = post.assets && Array.isArray(post.assets) && post.assets.length > 0;
    const hasMedia = post.media && Array.isArray(post.media) && post.media.length > 0;

    if (hasMedia) {
      return post.media;
    }

    if (hasAssets) {
      return post.assets.map(asset => ({
        src: asset.key,
        type_file: asset.type,
        caption: post.caption,
        duration: asset.duration,
        poster: asset.poster
      }));
    }

    return null;
  }

  // For non-bundle content, return media array if it exists
  return post.media || null;
};

/**
 * Generates a unique gradient background for feed items based on a seed value
 * @param {string|number} seed - A unique identifier (postId, index, post._id, etc.)
 * @returns {string} - CSS linear-gradient string
 */
export const getGradientBackground = (seed) => {
  // Use seed or generate a random one if not provided
  const seedValue = seed || Math.random();
  const hash = String(seedValue).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  // Define a palette of gradient color combinations
  const gradients = [
    "g-1",
    "g-2",
    "g-3",
    "g-4",
    "g-5",
    "g-6",
    "g-7",
    "g-8",
    "g-9",
  ];

  // Use hash to select a gradient (ensures same seed always gets same gradient)
  const gradientIndex = hash % gradients.length;
  return gradients[gradientIndex];
};

const gradientImageContext = require.context(
  '../../assets/img/g-images',
  false,
  /\.png$/
);

export const getGradientBackgroundImage = (seed) => {
  const gradient = getGradientBackground(seed);
  return gradientImageContext(`./${gradient}.png`);
};

// Utility function to calculate days remaining
export const calculateDaysRemaining = (expireAt) => {
  const expireDate = new Date(expireAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expireDate.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;
  // const daysRemaining = Math.max(0, Math.round((expireDate.getTime() - today.getTime()) / msPerDay));
  const daysRemaining = Math.round((expireDate.getTime() - today.getTime()) / msPerDay);
  return daysRemaining;
};

//  If a subscription is active the text that says “Renews Aug 22” is green (#49de80) 
// If a subscription is expiring soon it says “Ends Aug 22” in yellow (#ebb305) 
// If a subscription is expired it says “Expired Aug 22” in gray (#737373)
export const getSubscriptionDateText = (expire_at, state, tab = null, isEnd = false) => {
  if (!expire_at) return '';
  // subscriptions = "49de80"
  // expiresoon = "#ebb305"
  // expire = "#737373"
  // default = "#f534bf"

  const timezone = BROWSER_TIMEZONE;
  const expireDate = moment(expire_at).tz(timezone);
  // Treat expiry as end of day so subscription is valid for the entire expiry date (e.g. 4 Feb valid until end of 4 Feb; expired from 5 Feb)
  const expireEndOfDay = expireDate.clone().endOf('day');
  const isRenewing = moment().tz(timezone).isSameOrBefore(expireEndOfDay) && state === PURCHASE_STATE.ACTIVE;
  // const remainingDays = expireDate.diff(moment(), 'days');
  const daysRemaining = calculateDaysRemaining(expire_at);
  const isExpiringSoon = isRenewing && daysRemaining <= 3;
  const formattedDate = expireDate.format("MMM DD");

  let colorClass = '';
  let text = '';
  if (tab === 'subscriptions' || tab === 'all') {
    if (isEnd) {
      if (daysRemaining >= 0) {
        colorClass = 'expiresoon';
        text = `Ends ${formattedDate}`;
      } else {
        colorClass = 'expire';
        text = `Expired ${formattedDate}`;
      }
    } else if ((daysRemaining > 0 && daysRemaining <= 7) && !isExpiringSoon && state === PURCHASE_STATE.EXPIRED) {
      colorClass = 'expiresoon';
      // text = `Expiring ${formattedDate}`;
      text = `Ends ${formattedDate}`;
    } else {
      if (isExpiringSoon && state !== PURCHASE_STATE.ACTIVE) {
        colorClass = 'expiresoon';
        text = `Ends ${formattedDate}`;
      } else {
        if (state === PURCHASE_STATE.EXPIRED && daysRemaining >= 0) {
          colorClass = 'expiresoon';
          text = `Ends ${formattedDate}`;
        } else {
          colorClass = `${isRenewing ? 'subscriptions' : 'expire'}`;
          text = `${isRenewing ? 'Renews' : 'Expired'} ${formattedDate}`;
        }
      }
    }
  } else if (tab === 'expiresoon') {
    colorClass = 'expiresoon';
    text = `Ends ${formattedDate}`;
  } else if (tab === 'expire') {
    colorClass = 'expire';
    text = `Expired ${formattedDate}`;
  } else if (tab === 'all-creators') {
    colorClass = 'expire';
    if (isEnd) {
      if (daysRemaining >= 0) {
        text = `Ends ${formattedDate}`;
      } else {
        text = `Expired ${formattedDate}`;
      }
    } else {
      if (isRenewing) {
        text = `Renews ${formattedDate}`;
      } else if (state === PURCHASE_STATE.EXPIRED && daysRemaining >= 0) {
        text = `Ends ${formattedDate}`;
      } else {
        text = `Expired ${formattedDate}`;
      }
    }
  } else {
    colorClass = `${isRenewing ? 'default' : 'expire'}`;
    text = `${isRenewing ? 'Renews' : 'Expired'} ${formattedDate}`;
  }

  return <span className={colorClass}>{text}</span>;
};

/**
 * Determines if a post's content should be accessible to the current user.
 * @param {Object} params
 * @param {Object} params.post - The post object.
 * @param {boolean} params.isPPVContent - Flag indicating pay-per-view content.
 * @param {boolean} params.isSubContent - Flag indicating subscription-based content.
 * @param {Object|null} params.creatorInfo - Creator information including subscription status.
 * @returns {boolean} - True when the content should be unlocked.
 */
export const isPostAccessible = ({
  post,
  isPPVContent = false,
  isSubContent = false,
  creatorInfo = null,
} = {}) => {
  const isAdmin = isUserAdmin();
  if (isAdmin) {
    return true;
  }

  let isPending = false;
  if (post?.purchase?.state === PURCHASE_STATE.PENDING) {
    isPending = true;
  }
  if (creatorInfo?.subscription?.state === PURCHASE_STATE.PENDING) {
    isPending = true;
  }

  if (!post) return false;
  if (!post.lock_content) return true;
  if (isPending) return false;

  const hasPPVAccess = isPPVContent && (post.isPurchasedOrSubscribed ||
    (post?.purchase?._id && post?.purchase?.state === PURCHASE_STATE.ACTIVE));
  const hasSubscriptionAccess = isSubContent && (post.isPurchasedOrSubscribed || creatorInfo?.subscribed);

  return Boolean(hasPPVAccess || hasSubscriptionAccess);
};

// Helper function to randomly select n items from an array
export const getRandomItems = (array, n) => {
  if (array.length <= n) return array;
  const shuffled = [...array].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
};

// Helper function to extract the first error message from form errors
export const getFirstErrorMessage = (errors) => {
  if (!errors) return null;

  // Helper to recursively find first error message
  const findFirstError = (obj) => {
    if (!obj || typeof obj !== 'object') return null;

    // Check if this object has a message property
    if (obj.message && typeof obj.message === 'string') {
      return obj.message;
    }

    // Recursively search through object properties
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const result = findFirstError(obj[key]);
        if (result) return result;
      }
    }

    return null;
  };

  return findFirstError(errors);
};

export const formatCurrency = (value) => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0;
};

const USD_AMOUNT_DISPLAY_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const USD_AMOUNT_DISPLAY_FRACTIONAL_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Display-only USD string with grouping (e.g. 1244.5 → "1,244.50", 245 → "245"). Whole dollars omit decimals.
 * Do not wrap in Number() — use {@link formatAmountWithTwoDecimalPlacesNumber} for numeric payloads.
 */
export const formatUsdAmountDisplay = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  if (Number.isInteger(num)) {
    return USD_AMOUNT_DISPLAY_WHOLE_FORMATTER.format(num);
  }
  return USD_AMOUNT_DISPLAY_FRACTIONAL_FORMATTER.format(num);
};

const USD_AMOUNT_DISPLAY_CENTS_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** USD display with grouping and fixed cents (e.g. 1010 → "1,010.00"). Display-only — do not parse with Number(). */
export const formatUsdAmountDisplayCents = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return USD_AMOUNT_DISPLAY_CENTS_FORMATTER.format(num);
};

/** Format tip amount: whole numbers as "15", decimals as "15.60" or "15.65" (always 2 decimal places when fractional). */
export const formatAmountWithTwoDecimalPlaces = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2);
};

export const formatAmountWithTwoDecimalPlacesNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return Number(0);
  if (Number.isInteger(num)) return Number(num);
  return Number(num.toFixed(2));
};

export const sanitizeUrl = (urlRaw) => {
  if (!urlRaw || typeof urlRaw !== 'string') return null;
  try {
    return new URL(urlRaw).toString();
  } catch (e) {
    return null;
  }
};

/**
 * Copies text to clipboard. Falls back to a prompt if clipboard API fails.
 * Returns true only when the Clipboard API succeeds.
 *
 * @param {string} text
 * @param {string} promptLabel
 * @returns {Promise<boolean>}
 */
export const copyTextToClipboard = async (text, promptLabel = 'Copy this text:') => {
  if (!text || typeof text !== 'string') return false;

  try {
    if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // ignore; use fallback prompt below
  }

  try {
    if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
      window.prompt(promptLabel, text);
    }
  } catch (e) {
    // ignore
  }

  return false;
};

export const normalizeStatus = (status) => {
  if (typeof status !== 'string') return '';
  return status.trim().toUpperCase();
};

/**
 * Determines whether a given country value represents a US-based user.
 * Accepts common variations and is case-insensitive.
 *
 * @param {string} country
 * @returns {boolean}
 */
export const isUsCountry = (country) => {
  if (!country || typeof country !== 'string') return false;
  const normalized = country.trim().toLowerCase();

  if (!normalized) return false;

  // Common variants for United States
  const usValues = new Set([
    'us',
    'usa',
    'u.s.',
    'u.s.a.',
    'united states',
    'united states of america',
  ]);

  return usValues.has(normalized);
};

export const getPendingSigningUrl = (taxBanditsResponse) => {
  const status = normalizeStatus(taxBanditsResponse?.Status);
  const signingUrl = sanitizeUrl(taxBanditsResponse?.SigningUrl);
  if (status === 'PENDING' && signingUrl) return signingUrl;
  return null;
};

/**
 * Trigger a browser download (or open) for a direct file URL.
 * Note: Some browsers ignore `download` for cross-origin URLs and will open the file instead.
 *
 * @param {string} url
 * @param {string} [fileName]
 */
export const downloadFileFromUrl = (url, fileName = 'download') => {
  if (!url || typeof url !== 'string') return;

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const getCdnProfileUrl = (url, defaultUrl = null) => {
  if (!url) return defaultUrl;

  const CDN_URL = process.env.REACT_APP_CDN_URL;

  if (!CDN_URL) {
    return url;
  }

  try {
    const base = (CDN_URL || "").replace(/\/+$/, "");
    const PROFILE_CDN_PREFIX = base ? `${base}/profile-images` : "";

    const parsed = new URL(url);
    const pathSegments = parsed.pathname.split("/").filter(Boolean);

    let objectPathSegments = pathSegments;
    if (pathSegments[0] === "profile-images") {
      objectPathSegments = pathSegments.slice(1);
    } else if (pathSegments[0]?.includes("profile-images")) {
      // Handles object-storage bucket paths like /juicyhouse-profile-images-stg/image/...
      objectPathSegments = pathSegments.slice(1);
    }

    const objectPath = `/${objectPathSegments.join("/")}`;

    return `${PROFILE_CDN_PREFIX}${objectPath}${parsed.search}`;
  } catch (err) {
    return url;
  }
};


export const getCdnProfileUrl_19_Mar_2026 = (url, defaultUrl = null) => {
  if (!url) return defaultUrl;

  const CDN_URL = process.env.REACT_APP_CDN_URL;

  if (!CDN_URL) {
    return url;
  }

  try {
    const base = (CDN_URL || "").replace(/\/+$/, "");
    const PROFILE_CDN_PREFIX = base ? `${base}/profile-images` : "";

    const parsed = new URL(url);

    const pathSegments = parsed.pathname.split("/");
    const objectPath =
      pathSegments[1] === "profile-images"
        ? "/" + pathSegments.slice(2).join("/")
        : parsed.pathname;

    return `${PROFILE_CDN_PREFIX}${objectPath}${parsed.search}`;
  } catch (err) {
    return url;
  }
};